<template>
  <div class="demo-page" data-test="demo-landing">
    <b-container>
      <Widget class="mx-auto demo-widget">
        <h5 class="demo-logo">
          <i class="fa fa-circle text-warning" />
          <img src="../../assets/teamio-logo.png" alt="Teamio" width="100" />
          <i class="fa fa-circle text-warning" />
        </h5>

        <div v-if="state === 'working'" class="demo-body" data-test="demo-working">
          <b-spinner variant="primary" label="Opening" />
          <h4>Opening your demo practice…</h4>
          <p class="text-muted">This takes a few seconds.</p>
        </div>

        <div v-else-if="state === 'confirm'" class="demo-body" data-test="demo-confirm">
          <h4>You're signed in to {{ practiceName || 'your practice' }}</h4>
          <p class="text-muted">
            Opening the demo will sign you out of {{ practiceName || 'your practice' }} in this browser.
            You can sign back in any time.
          </p>
          <div class="demo-actions">
            <b-button variant="primary" data-test="demo-confirm-continue" @click="signOutAndOpen">Sign out and open the demo</b-button>
            <b-button variant="outline-secondary" data-test="demo-confirm-stay" @click="stay">Stay signed in</b-button>
          </div>
        </div>

        <div v-else-if="state === 'link_used'" class="demo-body" data-test="demo-link-used">
          <h4>This demo link has already been used or has expired</h4>
          <p class="text-muted">
            Each demo link works once. Reply to the email we sent you and we'll send a new one, or book a call and
            we'll walk you through Teamio.
          </p>
          <div class="demo-actions">
            <b-button variant="primary" :href="bookACallUrl" target="_blank" rel="noopener noreferrer">Book a call</b-button>
          </div>
        </div>

        <div v-else-if="state === 'missing'" class="demo-body" data-test="demo-missing">
          <h4>Open your demo from the link we sent you</h4>
          <p class="text-muted">
            This page needs the full link from your email. Don't have one yet? Book a call and we'll set you up.
          </p>
          <div class="demo-actions">
            <b-button variant="primary" :href="bookACallUrl" target="_blank" rel="noopener noreferrer">Book a call</b-button>
          </div>
        </div>

        <div v-else-if="state === 'unavailable'" class="demo-body" data-test="demo-unavailable">
          <h4>The demo isn't available right now</h4>
          <p class="text-muted">Please try again in a few minutes.</p>
          <div class="demo-actions">
            <b-button v-if="code" variant="primary" data-test="demo-retry" @click="open">Try again</b-button>
            <b-button :variant="code ? 'outline-secondary' : 'primary'" :href="bookACallUrl" target="_blank" rel="noopener noreferrer">Book a call</b-button>
          </div>
        </div>
      </Widget>
    </b-container>
    <footer class="footer">
      {{ year }} &copy; Teamio.app
    </footer>
  </div>
</template>

<script>
// Demo site (Try Teamio) — the page the operator's one-time link opens: /demo#t=<code> (plan Task 6.3, decision 1a).
// The rules live in src/utils/demo/demoLanding.js; tests: cypress/node-tests/demo-landing.test.js.
import Widget from '@/components/Widget/Widget';
import { BOOK_A_CALL_URL, DEMO_LANDING_PATH } from '@/utils/demo/demoSession';
import { initialCode, stashPendingCode, outcomeOf, practiceNameFrom, safeSessionStorage } from '@/utils/demo/demoLanding';

export default {
  name: 'DemoLanding',
  components: { Widget },

  data() {
    return {
      state: 'working',
      code: null,
      practiceName: null,
      bookACallUrl: BOOK_A_CALL_URL,
      year: new Date().getFullYear(),
    };
  },

  created() {
    this.code = initialCode(window.location.hash, safeSessionStorage());
    // The code is single-use, but it still never stays in the address bar or the history.
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
    if (!this.code) {
      if (this.$store.getters['customer/isDemo'] && localStorage.getItem('authenticated') === 'true') {
        this.stay(); // already in the demo: back to it
        return;
      }
      this.state = 'missing';
      return;
    }
    this.open();
  },

  methods: {
    async open() {
      this.state = 'working';
      try {
        await this.$store.dispatch('customer/demoLogin', { code: this.code });
      } catch (err) {
        this.state = outcomeOf(err);
        if (this.state === 'confirm') this.practiceName = practiceNameFrom(localStorage.getItem('user'));
      }
    },

    async signOutAndOpen() {
      // Kept in THIS tab only; /demo reads it once after the sign-out lands back here.
      stashPendingCode(safeSessionStorage(), this.code);
      this.state = 'working';
      await this.$store.dispatch('customer/logout', { reload: true, next: DEMO_LANDING_PATH });
    },

    stay() {
      this.$router.push(localStorage.getItem('redirectPath') || '/app');
    },
  },
};
</script>

<style src="./Demo.scss" lang="scss" scoped />
